/**
 * Core types for the Breakout SDK
 *
 * All user-facing values use human-readable types:
 * - Amounts: number (not bigint)
 * - Times: Date (not timestamps)
 * - Prices: 0-1 range (not WAD)
 */
import type { Address, Hex } from "viem"

// =============================================================================
// Constants
// =============================================================================

export const USDC_DECIMALS = 6
export const SHARE_DECIMALS = 6
export const PRICE_DECIMALS = 18
export const BASE_CHAIN_ID = 8453

// =============================================================================
// Config
// =============================================================================

export interface BreakoutConfig {
	factory: Address
	usdc: Address
}

// =============================================================================
// Market Types
// =============================================================================

export interface Market {
	address: Address
	status: MarketStatus
	buckets: BucketInfo[]
	openTime: Date
	cutoffTime: Date
	feeBps: number
	liquidity: number
}

export interface BucketInfo {
	index: number
	price: number // 0-1 range
}

export type MarketStatus =
	| { type: "pending"; opensIn: number }
	| { type: "open"; closesIn: number }
	| { type: "closed" }
	| { type: "resolved"; winner: number }
	| { type: "finalized"; winner: number }
	| { type: "paused"; pausedAt: Date }

// Contract status enum mapping
export const CONTRACT_STATUS = {
	PENDING: 0,
	OPEN: 1,
	CLOSED: 2,
	RESOLVED: 3,
	FINALIZED: 4,
} as const

// =============================================================================
// Position Types
// =============================================================================

export interface UserPosition {
	shares: number[] // Per bucket
	value: number // Current USDC value
	claimable: number // USDC claimable if finalized
	claimed: boolean
}

// =============================================================================
// Quote Types
// =============================================================================

export interface BuyQuote {
	// Input
	usdcIn: number

	// Output
	sharesOut: number

	// Cost breakdown
	cost: number // Before fee
	fee: number
	total: number // cost + fee

	// Price info
	avgPrice: number // cost / sharesOut
	priceImpact: number // How much this trade moves price (0.01 = 1%)
	priceBefore: number // Bucket price before trade
	priceAfter: number // Bucket price after trade
}

export interface SellQuote {
	// Input
	sharesIn: number

	// Output
	usdcOut: number // After fee

	// Breakdown
	proceeds: number // Before fee
	fee: number

	// Price info
	avgPrice: number
	priceImpact: number
	priceBefore: number
	priceAfter: number
}

// =============================================================================
// Signing Types (EIP-712)
// =============================================================================

export interface SigningPayload {
	domain: EIP712Domain
	types: Record<string, readonly EIP712Type[]>
	primaryType: string
	message: Record<string, unknown>
}

export interface EIP712Domain {
	name: string
	version?: string // Optional - Permit2 doesn't use version
	chainId: number
	verifyingContract: Address
}

export interface EIP712Type {
	name: string
	type: string
}

// =============================================================================
// Preparation Types (Gasless Flow)
// =============================================================================

export interface BuyPreparation {
	type: "buy"
	signingPayload: SigningPayload
	quote: BuyQuote
	expiresAt: Date
	/** @internal Contract-scale values for execution */
	_execution: BuyExecutionParams
}

export interface SellPreparation {
	type: "sell"
	signingPayload: SigningPayload
	quote: SellQuote
	expiresAt: Date
	/** @internal */
	_execution: SellExecutionParams
}

export interface ClaimPreparation {
	type: "claim"
	signingPayload: SigningPayload
	claimable: number
	expiresAt: Date
	/** @internal */
	_execution: ClaimExecutionParams
}

/**
 * Unified Preparation type - discriminated union of all preparation types.
 * Use `preparation.type` to narrow to specific type.
 */
export type Preparation = BuyPreparation | SellPreparation | ClaimPreparation

// Internal execution params (contract scale)
export interface BuyExecutionParams {
	market: Address
	user: Address
	bucket: number
	/** Total USDC budget (what user is willing to spend) */
	usdcAmount: bigint
	/** Minimum shares to receive (slippage protection) */
	minShares: bigint
	deadline: bigint
	usdcAddress: Address
	// Permit2 fields
	permit2Nonce: bigint
}

export interface SellExecutionParams {
	market: Address
	user: Address
	bucket: number
	shares: bigint
	minProceeds: bigint
	nonce: bigint
	deadline: bigint
}

export interface ClaimExecutionParams {
	market: Address
	user: Address
	nonce: bigint
	deadline: bigint
}

// =============================================================================
// Batch Preparation Types
// =============================================================================

export interface BatchBuyPreparation {
	type: "batch-buy"
	signingPayload: SigningPayload
	items: Array<{
		market: Address
		bucket: number
		quote: BuyQuote
	}>
	totals: {
		usdcIn: number
		fees: number
	}
	expiresAt: Date
	/** @internal */
	_execution: BatchBuyExecutionParams
}

export interface BatchBuyExecutionParams {
	markets: Address[]
	buckets: number[]
	usdcAmounts: bigint[]
	minSharesArray: bigint[]
	deadline: bigint
	usdcAddress: Address
	permitNonce: bigint
	totalPermitAmount: bigint
}

// =============================================================================
// Safe Transaction Types (Multisig)
// =============================================================================

export interface SafeTransaction {
	to: Address
	value: string
	data: Hex
}

export interface SafeTransactionBatch {
	version: "1.0"
	chainId: string
	meta: { name: string; description?: string }
	transactions: SafeTransaction[]
}

// =============================================================================
// Resolution Types
// =============================================================================

export interface Resolution {
	winningOutcome: number
	isResolved: boolean
	isFinalized: boolean
	dataUri: string
	nonce: number
}

export interface ResolutionParams {
	market: Address
	winningBucket: number
	/** URI of the resolution dataset (e.g., "ipfs://QmXxx...", "ar://...", or "https://...") */
	dataUri: string
}

// =============================================================================
// Fee Types
// =============================================================================

export interface AccumulatedFees {
	/** Protocol fees accumulated in USDC (human-readable) */
	protocol: number
}

// =============================================================================
// Transaction Result Types
// =============================================================================

export interface TransactionResult {
	txHash: Hex
	blockNumber: number
}

export interface CreateMarketResult extends TransactionResult {
	marketAddress: Address
}

export interface CreateMarketsResult extends TransactionResult {
	marketAddresses: Address[]
}

// =============================================================================
// Permit Types
// =============================================================================

export interface PermitSignature {
	v: number
	r: Hex
	s: Hex
}

export interface PermitParams {
	owner: Address
	spender: Address
	value: bigint
	nonce: bigint
	deadline: bigint
}
